import { useEffect, useRef, useState } from "react";

import styles from "./fba-edit-batch-item-drawer.module.css";
import {
  AddItemToInboundBatchBody,
  BxError,
  ErrorCode,
  LabelOwner,
  MskuPrepDetail,
  PatchInboundBatchItemBody,
  PrepCategory,
  PrepOwner,
  SyncJobStatus
} from "@boxem/core-api-schema";

import { Offer } from "@/api/products";
import { addItemToBatchV2, getSupplierById, setPrepDetails, updateItemInBatchV2 } from "@/api/fulfillment";
import { FbaBatchItem, useShipmentContext } from "@/contexts/ShipmentContext";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { Product, SellerProductData, useInventoryContext } from "@/contexts/InventoryContext";
import { logRocketError } from "@/utils/logUtils";
import Tooltip from "@/components/Tooltip";
import Button from "@/components/forms/Button/Button";
import IconInfo from "@/components/icons/IconInfo";
import Select from "@/components/forms/Select/Select";
import Autocomplete from "@/components/forms/Autocomplete/Autocomplete";
import InputNumber from "@/components/forms/InputNumber/InputNumber";
import DatePicker from "@/components/forms/DatePicker";
import Flex from "@/components/Flex/Flex";
import Alert from "@/components/Alert/Alert";
import Divider from "@/components/Divider/Divider";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import CopyToClipBoard from "@/components/CopyToClipboard/CopyToClipboard";
import Tabs, { TabItem } from "@/components/Tabs/Tabs";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";
import ProductDetails from "@/app/fba/ProductDetails/ProductDetails";
import CurrentOffers from "@/app/fba/CurrentOffers/CurrentOffers";
import {
  productConditionToString,
  categoryProbablyRequiresExpirationDate,
  generateListPriceOptions,
  relcalculateListPriceHelpText,
  ListPriceOption,
} from "@/app/fba/utils";

// given some prep details return the list of allowed prep owner values and label owner values
// for an item with these prep details. optionally, returns |valid| indicating passed values
// would be valid 
export function prepOwnerAndLabelOwnerValidForPrepDetails(
  prepDetails: MskuPrepDetail,
  prepOwner?: PrepOwner,
  labelOwner?: LabelOwner,
): { valid: boolean, allowedPrepOwners: PrepOwner[], suggestedPrepOwner: PrepOwner, allowedLabelOwners: LabelOwner[], suggestedLabelOwner: LabelOwner } {

  // from the docs: "Appropriate prepOwner values are returned in prepOwnerConstraint 
  // and appropriate labelOwner values are returned in labelOwnersConstraint. If these 
  // fields are empty in the response, you can select AMAZON or SELLER."
  // let prepOwners: PrepOwner[] = [PrepOwner.SELLER,PrepOwner.AMZON];
  // let labelOwners: LabelOwner[] = [LabelOwner.SELLER,LabelOwner.AMZON];

  // @HACK(nick) despite documentation saying these situations should not be possible, there are unfortunately
  // at least some cases of Amazon returning a label-owner constraint of 'NONE' (meaning 'NONE' should be the
  // only valid option) but the API then erroring saying 'Seller' or 'Amazon' is required. This ticket has this:
  // https://app.intercom.com/a/inbox/s7zia6wi/inbox/shared/mentions/conversation/3394?view=List
  // the reverse is also true occasionally, where the API says you should pass 'Seller' or 'Amazon', but in practice
  // it will actually only accept 'NONE'. This means the only way to be reliable is to allow the user all options in
  // all cases. We'll still try and be smart about autofilling/suggesting the 'correct' option:
  let prepOwners = [ PrepOwner.SELLER, PrepOwner.AMZON, PrepOwner.NONE ];
  let labelOwners = [ LabelOwner.SELLER, LabelOwner.AMZON, LabelOwner.NONE ];
  let suggestedPrepOwner = PrepOwner.SELLER;
  let suggestedLabelOwner = LabelOwner.SELLER;

  if (!prepDetails) {
    const valid = (!prepOwner || prepOwners.includes(prepOwner)) && (!labelOwner || labelOwners.includes(labelOwner));
    return { valid, allowedPrepOwners: prepOwners, suggestedPrepOwner, allowedLabelOwners: labelOwners, suggestedLabelOwner };
  }

  let prepOwnerAndLabelOwnerMustMatch: boolean = false;
  if (prepDetails.allOwnersConstraint) {
    prepOwnerAndLabelOwnerMustMatch = true;
  }

  switch (prepDetails.labelOwnerConstraint) {
    case "AMAZON_ONLY":
      suggestedLabelOwner = LabelOwner.AMZON;
      break;
    case "NONE_ONLY":
      suggestedLabelOwner = LabelOwner.NONE;
      break;
    case "SELLER_ONLY":
      suggestedLabelOwner = LabelOwner.SELLER;
      break;
  }

  if (prepOwnerAndLabelOwnerMustMatch) {
    // prepOwners = [...labelOwners] as any; // @HACK @FRAGILE
 
  } else {
    switch (prepDetails.prepOwnerConstraint) {
      case "AMAZON_ONLY":
        suggestedPrepOwner = PrepOwner.AMZON;
        break;
      case "NONE_ONLY":
        suggestedPrepOwner = PrepOwner.NONE;
        break;
      case "SELLER_ONLY":
        suggestedPrepOwner = PrepOwner.SELLER;
        break;
    }
  }

  const valid = (!prepOwner || prepOwners.includes(prepOwner)) && (!labelOwner || labelOwners.includes(labelOwner));
  return { valid, allowedPrepOwners: prepOwners, suggestedPrepOwner, allowedLabelOwners: labelOwners, suggestedLabelOwner };
}

// https://sellercentral.amazon.com/help/hub/reference/GF4G7547KSLDX2KC
const prepCategoryToStrings = (pc: PrepCategory): { name: string, tooltipText: string, tooltip?: React.ReactNode } => {
  switch (pc) {
    default:
      logRocketError(`Unknown prep category: ${pc}`);
      return { name: "", tooltipText: "" };

    case "FC_PROVIDED":
      return {
        name: "Set by Fulfillment Center",
        tooltipText: "Prep has been defined by the Fulfillment Center. This product doesn't need to set a prep category.",
      };
    case "UNKNOWN":
      return {
        name: "UNKNOWN",
        tooltipText: "",
      };
    case "NONE":
      return {
        name: "No prep needed",
        tooltipText: "This product does not require any special prep; no other prep category is appropriate.",
      };

    case "ADULT":
      return {
        name: "Adult",
        tooltipText: "Displays potentially offensive material such as profanity or nudity. E.g., Products that contain pictures of live, nude models, packaging that has obscene or profane messaging, etc.",
        tooltip: <Flex direction="column" gap={2}>
          <p>Displays potentially offensive material such as profanity or nudity. E.g., Products that contain pictures of live, nude models, packaging that has obscene or profane messaging, etc.</p>
          <br/>
          <h3>Guidance:</h3>
          <ol style={{listStyle:"initial",lineHeight:"2",marginTop:0}}>
            <li>Place product in a black or opaque bag</li>
            <li>Bag must have a suffocation warning</li>
            <li>Bag must be secured if not self-adhesive</li>
            <li>Barcode must be scannable</li>
          </ol>
        </Flex>
      };
    case "BABY":
      return {
        name: "Baby",
        tooltipText: "Made for a child aged three years or younger, packaging with cutouts greater than one square inch. E.g., Teething rings, bibs, exposed toys etc.",
        tooltip: <Flex direction="column" gap={2}>
          <p>Made for a child aged three years or younger, packaging with cutouts greater than one square inch. E.g., Teething rings, bibs, exposed toys etc.</p>
          <h3>Guidance:</h3>
          <ol style={{listStyle:"initial",lineHeight:"2",marginTop:0}}>
            <li>Place product in a transparent poly bag</li>
            <li>Bag must have a suffocation warning</li>
            <li>Bag must be secured if not self-adhesive</li>
            <li>Barcode must be scannable</li>
          </ol>
        </Flex>,
      };
    case "FRAGILE":
      return {
        name: "Fragile, glass",
        tooltipText: "Glass or otherwise fragile, or a liquid in a glass container. E.g., Glass, china, picture frames, clocks, mirrors, bottles of olive oil, etc.",
        tooltip: <Flex direction="column" gap={2}>
          <span>Glass or otherwise fragile, or a liquid in a glass container. E.g., Glass, china, picture frames, clocks, mirrors, bottles of olive oil, etc.</span>
          <h3>Guidance:</h3>
          <ol style={{listStyle:"initial",lineHeight:"2",marginTop:0}}>
            <li>Place product in bubble wrap or a bubble bag to prevent damage. Products can also be placed in a protective overbox</li>
            <li>Product bag or box must be secured or taped</li>
            <li>Product can pass a drop test on a hard surface without breaking</li>
            <li>Barcode must be scannable</li>
          </ol>
        </Flex>,
      };
    case "GRANULAR":
      return {
        name: "Powder, pellets, and granular",
        tooltipText: "Made of powder, pellets, or granular material. E.g., Facial powder, sugar, powdered detergents, etc.",
        tooltip: <Flex direction="column" gap={2}>
          <p>Made of powder, pellets, or granular material. E.g., Facial powder, sugar, powdered detergents, etc.</p>
          <h3>Guidance:</h3>
          <ol style={{listStyle:"initial",lineHeight:"2",marginTop:0}}>
            <li>Seal product in a transparent poly bag. The poly bag must include a suffocation warning</li>
            <li>Barcode must be scannable without having to open or unwrap the unit</li>
          </ol>
        </Flex>,
      };
    case "HANGER":
      return {
        name: "Apparel on hanger",
        tooltipText: "Made of cloth or fabric and intended to be put on a hanger. E.g., Scarves, shirts, jackets, etc.",
        tooltip: <Flex direction="column" gap={2}>
          <p>Made of cloth or fabric and intended to be put on a hanger. E.g., Scarves, shirts, jackets, etc.</p>
          <h3>Guidance:</h3>
          <ol style={{listStyle:"initial",lineHeight:"2",marginTop:0}}>
            <li>Remove hanger, and then place product in a transparent poly bag to protect from damage or dust</li>
            <li>Products can also be shrink-wrapped</li>
            <li>Bag must have a suffocation warning</li>
            <li>Bag must be secured if not self-adhesive</li>
            <li>Barcode must be scannable</li>
          </ol>
        </Flex>,
      };
    case "LIQUID":
      return {
        name: "Liquids (non-glass)",
        tooltipText: "Liquid or viscous without a double seal. E.g., soap, spray bottles, lotion, honey, etc.",
        tooltip: <Flex direction="column" gap={2}>
          <p>Liquid or viscous without a double seal. E.g., soap, spray bottles, lotion, honey, etc.</p>
          <h3>Guidance:</h3>
          <ol style={{listStyle:"initial",lineHeight:"2",marginTop:0}}>
            <li>Place product in a transparent poly bag to protect from leakage</li>
            <li>Bag must have a suffocation warning</li>
            <li>Bag must be secured if not self-adhesive</li>
            <li>Barcode must be scannable</li>
          </ol>
        </Flex>,
      };
    case "PERFORATED":
      return {
        name: "Perforated packaging",
        tooltipText: "In packaging that has a perforated opening. E.g., Chocolate boxes, candy display boxes, etc.",
        tooltip: <Flex direction="column" gap={2}>
          <p>In packaging that has a perforated opening. E.g., Chocolate boxes, candy display boxes, etc.</p>
          <h3>Guidance:</h3>
          <ol style={{listStyle:"initial",lineHeight:"2",marginTop:0}}>
            <li>Seal product in a transparent poly bag. The poly bag must include a suffocation warning</li>
            <li>Barcode must be scannable without having to open or unwrap the unit</li>
          </ol>
        </Flex>,
      };
    case "SET":
      return {
        name: "Sold as set",
        tooltipText: "Multiple items that are sold as one unit. E.g., Bundled items, Multi-packs, Toy sets, Case of bottled drinks, etc.",
        tooltip: <Flex direction="column" gap={2}>
          <p>Multiple items that are sold as one unit. E.g., Bundled items, Multi-packs, Toy sets, Case of bottled drinks, etc.</p>
          <h3>Guidance:</h3>
          <ol style={{listStyle:"initial",lineHeight:"2",marginTop:0}}>
            <li>Add a label to the unit that clearly states that the products are to be received and sold as a single unit. For example, "Sold as set," "Ready to ship," or "This is a set. Do not separate."</li>
            <li>Provide a unique ASIN for the set if the individual units in the set already have an ASIN</li>
            <li>The barcodes on the individual units must not face outward or the barcodes must be covered</li>
            <li>The set's barcode must be scannable</li>
          </ol>
        </Flex>,
      };
    case "SHARP":
      return {
        name: "Sharp",
        tooltipText: "Sharp and easily exposed, not already contained in protective packaging. E.g., Scissors, tools, metal raw materials, etc.",
        tooltip: <Flex direction="column" gap={2}>
          <p>Sharp and easily exposed, not already contained in protective packaging. E.g., Scissors, tools, metal raw materials, etc.</p>
          <h3>Guidance:</h3>
          <ol style={{listStyle:"initial",lineHeight:"2",marginTop:0}}>
            <li>Place product in bubble wrap or a bubble bag to keep it from being exposed. Products can also be placed in a protective overbox</li>
            <li>Ensure that the packaging is secured and that any sharp points or edges are completely covered</li>
            <li>Barcode must be scannable</li>
          </ol>
        </Flex>,
      };
    case "SMALL":
      return {
        name: "Small",
        tooltipText: "Longest side less than 2 1/8 inches (width of a credit card). E.g., Jewelry, keychains, flash drives, etc.",
        tooltip: <Flex direction="column" gap={2}>
          <p>Longest side less than 2 1/8 inches (width of a credit card). E.g., Jewelry, keychains, flash drives, etc.</p>
          <h3>Guidance:</h3>
          <ol style={{listStyle:"initial",lineHeight:"2",marginTop:0}}>
            <li>Place product in a transparent poly bag</li>
            <li>Bag must have a suffocation warning</li>
            <li>Bag must be secured if not self-adhesive</li>
            <li>Barcode must be scannable</li>
          </ol>
        </Flex>,
      };
    case "TEXTILE":
      return {
        name: "Apparel, fabric, plush, and textiles",
        tooltipText: "Made of cloth or fabric that could be damaged by dirt, dust, moisture, or liquid. E.g., Shirts, bags, belts, plush toys such as teddy bears, etc.",
        tooltip: <Flex direction="column" gap={2}>
          <p>Made of cloth or fabric that could be damaged by dirt, dust, moisture, or liquid. E.g., Shirts, bags, belts, plush toys such as teddy bears, etc.</p>
          <h3>Guidance:</h3>
          <ol style={{listStyle:"initial",lineHeight:"2",marginTop:0}}>
            <li>Place product in a transparent poly bag to protect from damage or dust</li>
            <li>Products can also be shrink-wrapped</li>
            <li>Bag must have a suffocation warning</li>
            <li>Bag must be secured if not self-adhesive</li>
            <li>Barcode must be scannable</li>
          </ol>
        </Flex>,
      };
  }
}

type FbaBatchItemDrawerProps = {
  mode: "UPDATE_ITEM_IN_BATCH" | "ADD_TO_BATCH";
  prepDetails?: MskuPrepDetail;
  onClose: () => void;

   // set this if mode is 'UPDATE_ITEM_IN_BATCH':
  item?: FbaBatchItem;
  
  // set these two if mode is 'ADD_TO_BATCH':
  product?: Product;
  sellerDataIndex?: number; // index on product.sellerData that we care about

};

export interface BatchItemForm {
  id?: number; // only set for the update path

  price?: ListPriceOption;
  quantity?: number;
  buyCost?: number;

  supplier?: string;
  datePurchased?: string;

  expirationDate?: string;
  conditionNotes?: string;

  originalPrepCategory?: PrepCategory;
  prepCategory?: PrepCategory;
  prepOwner?: PrepOwner;
  labelOwner?: LabelOwner;

  // these are set when adding a new item to a batch:
  product?: Product;
  sellerDataIndex?: number;
}

const FbaBatchItemDrawer = ({
  mode,
  product,
  sellerDataIndex,
  item,
  prepDetails,
  onClose,
}: FbaBatchItemDrawerProps) => {
  const { currentBatch, setCurrentBatch } = useShipmentContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const { suppliers, setSuppliers, clearPrepDetailsForSKUs, getPrepDetailsForSKUs, getOffersForASIN, getSalesRanksForASINs } = useInventoryContext();

  const [activeTab, setActiveTab] = useState<"form" | "offers" | "details">("form");

  const [offersLoading, setOffersLoading] = useState<boolean>(true);
  const [offers, setOffers] = useState<Offer[]>([]);
  
  const [supplierOptions, setSupplierOptions] = useState<string[]>([]);

  const [listPriceOptions, setListPriceOptions] = useState<ListPriceOption[]>([]);
  const [listPriceHelpText, setListPriceHelpText] = useState<string>("");

  const [listPriceInvalidText, setListPriceInvalidText] = useState<string | null>(null);
  const [quantityInvalidText, setQuantityInvalidText] = useState<string | null>(null);
  const [expirationDateInvalidText, setExpirationDateInvalidText] = useState<string | null>(null);
  const [datePurchasedInvalidText, setDatePurchasedInvalidText] = useState<string | null>(null);
  const [prepCategoryInvalidText, setPrepCategoryInvalidText] = useState<React.ReactNode | null>(null);

  const [addItemErrors, setAddItemErrors] = useState<BxError[]>([]);
  const [allowedPrepOwners, setAllowedPrepOwners] = useState<PrepOwner[]>([PrepOwner.SELLER,PrepOwner.AMZON]);
  const [allowedLabelOwners, setAllowedLabelOwners] = useState<LabelOwner[]>([LabelOwner.SELLER,LabelOwner.AMZON,LabelOwner.NONE])

  const [form, setForm] = useState<BatchItemForm>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [invalid, setInvalid] = useState<boolean>(false);

  const asyncEventCounter = useRef(0);

  // set form default values & allowed prep & label owners:
  useEffect(() => {

    const allowedPrepAndLabelOwners = prepOwnerAndLabelOwnerValidForPrepDetails(prepDetails);
    setAllowedPrepOwners(allowedPrepAndLabelOwners.allowedPrepOwners);
    setAllowedLabelOwners(allowedPrepAndLabelOwners.allowedLabelOwners);

    let itemErrors: BxError[] = [];
    if (!!product && sellerDataIndex !== undefined && product.sellerData[sellerDataIndex].status) {
      switch (product.sellerData[sellerDataIndex].status) {
        case "INCOMPLETE":
          itemErrors.push({
            message: "This product may not have any elligible offers. This can happen because this product requires approval to list. You may encounter issues if you proceed with this shipment.",
            actions: [
              {
                link: `https://sellercentral.amazon.com/myinventory/inventory?fulfilledBy=all&searchField=all&searchTerm=${encodeURIComponent(product.sellerData[sellerDataIndex].sku)}`,
                description: "View product listing status on seller central",
              }
            ]
          });
          break;

        case "INACTIVE":
          // this status is very common, (over 6.5k in Miles' inventory), but it's not clear what it indicates.
          // most appear to be elligible for sale, some are missing an offer (like 'INCOMPLETE').
          // we should fix this status so it actually means something - we actually would like to know if a listing
          // is 'INACTIVE'.
          break;
      }
    }
    if (!!product && !!product?.hazmat) {
      itemErrors.push({
        message: "This product is classified as dangerous goods (hazmat), which means it is or contains substances or materials that may pose a risk during storing, handling, or transporting. It may not be elligible to be sent FBA. In some cases, the product may just require some additional information to be provided via seller-central.",
        actions: [
          {
            link: "https://sell.amazon.com/blog/amazon-hazmat-dangerous-goods",
            description: "Read More about Dangerous Goods on Amazon", 
          },
          {
            link: `https://sellercentral.amazon.com/myinventory/inventory?fulfilledBy=all&page=1&pageSize=25&searchField=all&searchTerm=${product.sellerData[sellerDataIndex].sku}&sort=date_created_desc&status=all&ref_=xx_invmgr_dnav_xx`,
            description: `View SKU: ${product.sellerData[sellerDataIndex].sku} on Seller Central`,
          }
        ],
      });
    }
    setAddItemErrors(itemErrors);

    if (mode === "ADD_TO_BATCH" && product && sellerDataIndex !== undefined && suppliers) {
      setForm({
        price: product.sellerData[sellerDataIndex].listPrice ? { price: product.sellerData[sellerDataIndex].listPrice.toString() } : null,
        quantity: null,
        buyCost: product.sellerData[sellerDataIndex].buyCost ? +product.sellerData[sellerDataIndex].buyCost : null,
        product: product,
        sellerDataIndex: sellerDataIndex,
        originalPrepCategory: prepDetails?.prepCategory,
        prepCategory: prepDetails?.prepCategory,
        prepOwner: allowedPrepAndLabelOwners.suggestedPrepOwner,
        labelOwner: allowedPrepAndLabelOwners.suggestedLabelOwner,
      });
    } else if (mode === "UPDATE_ITEM_IN_BATCH" && item && suppliers) {
      setForm({
        id: item.id,
        price: item.listPrice ? { price: item.listPrice.toString() } : null,
        quantity: item.quantity,
        buyCost: item.buyCost,
        supplier: suppliers.find(supplier => supplier.id === item.supplierId)?.name,  
        datePurchased: item.datePurchased,
        expirationDate: item.expirationDate,  
        conditionNotes: item.conditionNotes,
        originalPrepCategory: prepDetails?.prepCategory,
        prepCategory: prepDetails?.prepCategory,
        prepOwner: item.prepOwner ?? allowedPrepAndLabelOwners.suggestedPrepOwner,
        labelOwner: item.labelOwner ?? allowedPrepAndLabelOwners.suggestedLabelOwner,
      });
    }
  }, [item, mode, product, sellerDataIndex, suppliers, prepDetails]);

  // generate supplier options from suppliers list (need a separate array because we can 
  // create temporary supplier options inside the autocomplete itself):
  useEffect(() => {
    if (suppliers) {
      setSupplierOptions(suppliers.map(supplier => supplier.name));
    }
  }, [suppliers]);

  // initiate loading of offers for the product:
  useEffect(() => {
    if (mode === "ADD_TO_BATCH" && !!product?.asin) {
      // setOffersLoading(true);
      getOffersForASIN(product.asin)
        .then(offers => {
          setListPriceOptions(generateListPriceOptions(offers));
          setOffers(offers);
        })
        .catch(e => {
        })
        .finally(() => {
          setOffersLoading(false);
        });
    }
  }, [product]);

  // validate form & set error text:
  useEffect(() => {
    // synchronously validate list price, quantity, prep category, date purchased:
    setListPriceInvalidText(null);
    setQuantityInvalidText(null);
    setPrepCategoryInvalidText(null);
    setDatePurchasedInvalidText(null);

    let invalid = false;
  
    if (!form.price?.price) {
      setListPriceInvalidText("List price is required");
      invalid = true;
    }

    if (!form.quantity || form.quantity <= 0) {
      setQuantityInvalidText("Quantity must be above 0");
      invalid = true;
    }

    if (!!form.datePurchased) {
      if (new Date(form.datePurchased) > new Date()) {
        setDatePurchasedInvalidText("If provided, date purchased must be in the past.");
        invalid = true;
      }
    }

    if (!form.prepCategory || form.prepCategory === "UNKNOWN") {
      setPrepCategoryInvalidText("Select the most appropriate prep category to proceed.");
      invalid = true;
    }

    setInvalid(invalid);

    // validate expiration date asynchronously (because we use a heuristic that involves sales ranks, which must be fetched async):
    // @NOTE(nick) if we made the whole validator async, I got some annoying race conditions where we'd capture an empty version of
    // the form, and we'd just generally experience incorrect form validation.
    const validateExpirationDate = async (form: BatchItemForm) => {
      const n = ++asyncEventCounter.current;
      const salesRanks = await getSalesRanksForASINs([product?.asin ?? item?.asin]);

      // if asyncEventCounter got incremented past `n`, that means we already initiated another sales rank request and responses
      // got delivered out of order. We should ignore all but the latest result. (This happens relatively commonly, because the
      // first request can happen when the form is empty.)
      if (n < asyncEventCounter.current) return;

      const expirationDateRequired = categoryProbablyRequiresExpirationDate(salesRanks[0]?.category);
      let expirationInvalidText = null;
      if (!!form.expirationDate) {
        const nintetyDaysInFuture = new Date();
        nintetyDaysInFuture.setDate(nintetyDaysInFuture.getDate() + 90);
        if (new Date(form.expirationDate) < nintetyDaysInFuture) {
          expirationInvalidText = "If provided, expiration date must be at least 90 days in the future.";
        }
      } else if (expirationDateRequired) {
        expirationInvalidText = `Items in the category '${product?.category ?? item?.category ?? salesRanks[0]?.category}' require an expiration date.`;
      }
      setExpirationDateInvalidText(expirationInvalidText);
      setInvalid(invalid || !!expirationInvalidText);
    }
    
    validateExpirationDate(form);
  }, [form, prepDetails]);

  // set list price help text based on offers/list price options:
  useEffect(() => {
    if (form.price?.price && !offersLoading && listPriceOptions && listPriceOptions.length > 0) {
      setListPriceHelpText(relcalculateListPriceHelpText(offersLoading, parseFloat(form.price?.price), listPriceOptions));
    }
  }, [form, offersLoading, listPriceOptions])

  // didn't really know where to put this, we call it after adding an item to a batch,
  // but also need to do so when refreshing on step 1 in some cases, and re-initiate it
  // in some error scenarios.
  const setPrepCategoryForItem = async (
    fbaItem: FbaBatchItem,
    prepCategory: PrepCategory,
  ) => {
    fbaItem.prepCategoryPollingStatus = SyncJobStatus.PROCESSING;

    for await (const response of setPrepDetails(fbaItem.sku, prepCategory)) {
      if (response.errors) {
        handleErrorResponseGenerically(response.errors);
      }
      if (response.data) {
        for (const item of response.data.items) {
          const batchItem = currentBatch.items.find(i => i.sku === item.msku);
          if (!batchItem) return;
          batchItem.prepCategoryPollingStatus = item.status;
          // the cache needs to be cleared because after setting the prep category, the prep details may now be different.
          // we'll refetch here:
          clearPrepDetailsForSKUs([batchItem.sku]); 
          getPrepDetailsForSKUs([batchItem.sku]);
        }
        setCurrentBatch({...currentBatch});
      }
      if (!response.keepPolling) {
        break;
      }
    }
  }

  const submit = () => {
    if (mode === "ADD_TO_BATCH") {
      setLoading(true);

      const originalPrepCategory = form.originalPrepCategory;
      const prepCategory = form.prepCategory;

      const p: SellerProductData = form.product.sellerData[form.sellerDataIndex];

      const input: AddItemToInboundBatchBody = {
        asin: form.product.asin,
        condition: p.condition,
        sku: p.sku.trim(),
        name: form.product.name,
        quantity: form.quantity,
        listPrice: form.price.price.toString(),

        // @NOTE the omission of prepCategory, it's updated via a separate API call just below
        prepOwner: form.prepOwner,
        labelOwner: form.labelOwner,

        // these are all optional:
        buyCost: form.buyCost?.toString() ?? "",
        supplier: form.supplier,
        datePurchased: form.datePurchased,
        expirationDate: form.expirationDate,
        conditionNotes: form.conditionNotes,
      };

      addItemToBatchV2(currentBatch.id, input)
        .then(async batchItem => {
          currentBatch.items.push(batchItem);
          setCurrentBatch({ ...currentBatch, items: [...currentBatch.items] });

          if (!input.supplierId && batchItem.supplierId) {
            // we just created a new supplier, add it to the local list of suppliers!
            setSuppliers([...suppliers, { name: form.supplier, id: batchItem.supplierId }]);
          }

          onClose();

          // if the prep category changed, we will have to set it:
          if (originalPrepCategory === "UNKNOWN" || originalPrepCategory !== prepCategory) {
            setPrepCategoryForItem(batchItem, prepCategory);
          }
        })
        .catch((errors: any) => {
          if (handleErrorResponseGenerically(errors)) {}
          setAddItemErrors(errors);
        })
        .finally(() => {
          setLoading(false)
        });

    } else if (mode === "UPDATE_ITEM_IN_BATCH") {
      setLoading(true);
      const originalPrepCategory = form.originalPrepCategory;
      const prepCategory = form.prepCategory;
      const dto: PatchInboundBatchItemBody = {
        ...form,
        id: form.id,
        listPrice: form.price?.price.toString() ?? undefined,
        buyCost: form.buyCost?.toString() ?? undefined,
        supplier: form.supplier ? form.supplier.trim() : undefined,
      };
      updateItemInBatchV2(currentBatch.id, dto)
        .then(result => {
          const idx = currentBatch.items.findIndex(item => item.id === result.id);
          if (idx !== -1) {
            const existingItem = currentBatch.items[idx];
            currentBatch.items[idx] = {
              ...existingItem,
              ...result,
              buyCost: result.buyCost ? +result.buyCost : existingItem.buyCost,
              listPrice: result.listPrice ? +result.listPrice : existingItem.listPrice,
              supplierId: result.supplierId ?? existingItem.supplierId,
            };
            if (result.supplierId) getSupplierById(result.supplierId).then(s => {
              if (suppliers.findIndex(p => p.id === s.id) < 0) {
                const newSuppliers = [...suppliers, s];
                setSuppliers(newSuppliers);
              }
            }); // make sure we have this supplier in the frontend list - we intentionally discard the result here

            // if the prep category changed, we will have to set it:
            if (originalPrepCategory === "UNKNOWN" || originalPrepCategory !== prepCategory) {
              setPrepCategoryForItem(existingItem, prepCategory);
            }
                        
            setCurrentBatch({...currentBatch});

            onClose();
          }
        })
        .catch(e => {
          if (handleErrorResponseGenerically(e)) {
            
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const visualErrors: { message: React.ReactNode; actions: {
    details?: any;
    description?: string;
    link: string;
  }[] }[] = [];
  addItemErrors?.forEach?.((error) => {
    let message;
    switch (error.type) {
      case ErrorCode.DUPLICATE_ITEM:
        message = "This item is already in your shipment.";
        break;
      
      case ErrorCode.ADD_TO_BATCH_LISTING_DOESNT_EXIST_OR_DELETED:
        message = "Amazon reports that this SKU may not exist in your seller central account. Use another SKU instead, listing a new SKU if necessary (this can be done by going Inventory -> List FBA in the left-sidebar). Handling will improve in the future to automatically remove non-existant SKUs from your inventory.";
        error.actions = [
          {
            link: `https://sellercentral.amazon.com/myinventory/inventory?fulfilledBy=all&searchField=all&searchTerm=${encodeURIComponent(product.sellerData[sellerDataIndex].sku)}`,
            description: "Search for inventory item on seller central",
          }
        ];
        break;

      default:
        message = error.message;
        break;
    }

    visualErrors.push({
      message,
      actions: error.actions,
    });
  });

  const batchForm = (
    <>
      <form className={styles.form}>
        {visualErrors.length > 0 && (
          <Alert
            className={styles.addItemErrorAlert}
            type="error"
            message={
              <>
                {visualErrors.map((ve, i) => {
                  return (
                    <Flex key={i} direction="column" gap={2}>
                      <span>{ve.message}</span>

                      {ve.actions?.map(a => (
                        <a href={a.link} target="_blank">
                          {a.description}
                        </a>
                      ))}
                    </Flex>
                  );
                })}
              </>
            }
          />
        )}

        <h1>Pricing & Profit</h1>

        <div className={styles.inputGroup}>
          <label htmlFor="listPrice">
            <Flex gap={2} justify="start" align="center">
              <Tooltip
                title="List price is the amount a potential buyer will see on the listing for this item on Amazon"
                placement="top-right"
              >
                <IconInfo/>
              </Tooltip>
              <span>List price:</span>
            </Flex>
          </label>

          <Flex direction="column" gap={2}>
            <Autocomplete
              prefix="$"
              type="number" //This does not make the "text input" a "number input". It changes the validation that happens and allows users to enter decimals.
              inputId="listPrice"
              disabled={loading}
              invalid={!!listPriceInvalidText}
              loading={offersLoading}
              placeholder={listPriceOptions.length === 0 ? "Type a list price" : "Type or select a list price"}
              value={form.price}
              onChange={(option) => setForm({...form, price: option })}
              onTextChange={text => setForm({...form, price: { price: text }})}
              options={listPriceOptions}
              optionToDisplayString={option => option.price?.toString()}
              optionRender={(option) => {
                return (
                  <Flex justify="space-between" style={{width:"100%"}}>
                    <span>${option.price}</span>
                    <Flex gap={2}>
                      {(option.isBuyBox && option.fba) && (
                        <Tooltip placement="bottom-left"  title="This is the FBA buy-box for this item">
                          <img src="/images/buy-box.svg" width={24} height={24} alt="buy box" />
                        </Tooltip>
                      )}
                      {option.isAmazon && (
                        <Tooltip placement="bottom-left" title="Amazon is a seller on the listing">
                          <img src="/images/amazon.svg" width={24} height={24} alt="amazon" />
                        </Tooltip>
                      )}
                      <Tooltip placement="bottom-left" title={option.fba ? "Fulfilled by Amazon" : "Fulfilled by Merchant"}>
                        <span className="weak">{option.fba ? "FBA" : "FBM"}</span>
                      </Tooltip>
                    </Flex>
                  </Flex>
                );
              }}
            />
          </Flex>
        </div>

        {listPriceInvalidText && <p className={`small-text ${styles.invalidText}`}>{listPriceInvalidText}</p>}
        {(!listPriceInvalidText && listPriceHelpText) && (<span className={"small-text weak " + styles.listPriceHelpText}>{listPriceHelpText}</span>)}

        {currentBatch.showBuyCostInput && (
          <div
            className={styles.inputGroup}
          >
            <label htmlFor="buyCost">
              <Flex gap={2} justify="start" align="center">
                <Tooltip
                  title={`Buy cost is the total amount you paid per unit of this item. We store this for you for analytics purposes`}
                  placement="top-right"
                >
                  <IconInfo/>
                </Tooltip>
                <span>Buy cost:</span>
              </Flex>
            </label>
            <InputNumber
              id="buyCost"
              disabled={loading}
              prefix="$"
              showControls={false}
              centerText={false}
              value={form.buyCost}
              onChange={v => setForm({...form, buyCost: v})}
            />
          </div>
        )}

        <div className={styles.inputGroup}>
          <label htmlFor="quantity">
            Quantity:
          </label>
          <InputNumber
            id="quantity"
            disabled={loading}
            invalid={!!quantityInvalidText}
            min={1}
            max={10000}
            value={form.quantity}
            onChange={v => setForm({...form, quantity: v})}
          />
        </div>

        {!!quantityInvalidText && (<span className={"small-text weak " + styles.invalidText}>{quantityInvalidText}</span>)}

        <Divider />

        {(currentBatch.showDatePurchasedInput || currentBatch.showSupplierInput) && <h1>Supplier info</h1>}

        {currentBatch.showSupplierInput && (
          <div className={styles.inputGroup}>
            <label htmlFor="supplier">
              Supplier:
            </label>
            <Autocomplete
              inputId="supplier"
              disabled={loading}
              options={supplierOptions}
              placeholder={supplierOptions.length === 0 ? "Type a supplier name" : "Type or select a supplier name"}
              value={form.supplier}
              onChange={v => {
                setForm({...form, supplier: v});
              }}
              onCreateNewOption={v => {
                const set = new Set([...supplierOptions, v])
                setSupplierOptions(Array.from(set));
                setForm({...form, supplier: v});
              }}
              showSearchIcon
            />
          </div>
        )}

        {currentBatch.showDatePurchasedInput && (
          <div className={styles.inputGroup}>
            <label htmlFor="datePurchased">
              Date purchased:
            </label>
            <Flex direction="column" gap={2}>
              <DatePicker
                id="datePurchased"
                disabled={loading}
                value={form.datePurchased ? new Date(form.datePurchased) : null}
                onChange={v => setForm({...form, datePurchased: v.toISOString()})}
              />
              {datePurchasedInvalidText && (<div className={`small-text ${styles.invalidText}`}>{datePurchasedInvalidText}</div>)}
            </Flex>
          </div>
        )}

        {(currentBatch.showDatePurchasedInput || currentBatch.showSupplierInput) && <Divider />}

        <h1>Product Details & Prep</h1>

        <div className={styles.inputGroup}>
          <label htmlFor="expirationDate">
            Expiration Date:
          </label>
          <Flex direction="column" gap={2}>
            <DatePicker
              id="expirationDate"
              disabled={loading}
              value={form.expirationDate ? new Date(form.expirationDate) : null}
              onChange={v => setForm({...form, expirationDate: v.toISOString()})}
            />
            {expirationDateInvalidText && (<div className={`small-text ${styles.invalidText}`}>{expirationDateInvalidText}</div>)}
          </Flex>
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="prepCategory">
            <Flex gap={2} justify="start" align="center">
              <Tooltip
                title={`A product's prep category determines a set of actions, or preparations, that are required to be done before products are elligible for sale. Depending on the prep owner, the preparations may be done by you before shipping, or by Amazon upon receiving (which may incur a fee)`}
                placement="top-right"
              >
                <IconInfo/>
                
              </Tooltip>
              <span>Prep Category:</span>
            </Flex>
          </label>
          <Flex direction="column" gap={2}>
            <Select
              id="prepCategory"
              fullWidth
              disabled={form.prepCategory === "FC_PROVIDED"}
              invalid={form.prepCategory === "UNKNOWN"}
              options={[
                // these two values are not valid as input:
                // "FC_PROVIDED",
                // "UNKNOWN",
                "NONE",
                "BABY",
                "FRAGILE",
                "GRANULAR",
                "HANGER",
                "LIQUID",
                "PERFORATED",
                "SET",
                "SHARP",
                "SMALL",
                "TEXTILE",
                "ADULT",
              ] as PrepCategory[]}
              optionRender={option => {
                switch (option) {
                  case "FC_PROVIDED":
                    return (
                      <span>Amazon Provided</span>
                    );

                  case "UNKNOWN":
                    return <span>Select a prep category</span>;
                }

                const r = prepCategoryToStrings(option);
                return <span>{r.name}</span>;
              }}
              selectionRender={option => {
                switch (option) {
                  case "FC_PROVIDED":
                    return (
                      <Tooltip
                        title={"Amazon has internally established a prep category for this MSKU, and it cannot be changed."}
                      >
                        <span>Amazon Provided</span>
                      </Tooltip>
                    );

                  case "UNKNOWN":
                    return <span>Select a prep category</span>;
                }

                const r = prepCategoryToStrings(option);
                return (
                  <Tooltip title={r.tooltipText}>
                    <Flex gap={2} align="center">
                      <IconInfo/>
                      <p>{r.name}</p>
                    </Flex>
                  </Tooltip>
                );
              }}
              value={form.prepCategory}
              onChange={v => setForm({...form, prepCategory: v })}
            />
          </Flex>
        </div>

        {!!prepCategoryInvalidText && (<span className={"small-text weak " + styles.invalidText}>{prepCategoryInvalidText}</span>)}

        <div className={styles.inputGroup}>
          <label htmlFor="prepOwner">
            <Flex gap={2} justify="start" align="center">
              <Tooltip
                title={`Certain items require prep, which can be the responsibility of the seller, Amazon, or no-one. When given the option, choosing 'Amazon' may incur a fee. Certain MSKUs may be constrained to have specific prep owner(s). For more information, see Amazon's "FBA Prep Service" documentation`}
                placement="top-right"
              >
                <IconInfo/>
              </Tooltip>
              <span>Prep Owner:</span>
            </Flex>
          </label>
          <Flex direction="column" gap={2}>
            <Tooltip
              title={!!prepDetails?.prepOwnerConstraint ? "Amazon has constrained the prep owner to be this value - it can't be changed." : ""}
            >
              <Select
                fullWidth
                options={allowedPrepOwners}
                optionRender={option => {
                  switch (option) {
                    case PrepOwner.AMZON:
                      return (
                        <span>Amazon</span>
                      );
                    case PrepOwner.NONE:
                      return (
                        <span>Prep not required</span>
                      );
                    case PrepOwner.SELLER:
                      return (
                        <span>Seller</span>
                      );
                  }
                }}
                value={form.prepOwner}
                onChange={v => setForm({...form, prepOwner: v })}
              />
            </Tooltip>
          </Flex>
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="labelOwner">
            <Flex gap={2} justify="start" align="center">
              <Tooltip
                title={`For eligible products that require a barcode label, you can have Amazon apply those barcode labels for you for a per-item fee. Certain MSKUs may be constrained to have specific label owner(s). For more information, see Amazon's "FBA Label Service" documentation`}
                placement="top-right"
              >
                <IconInfo/>   
              </Tooltip>
              <span>Label Owner:</span>
            </Flex>
          </label>
          <Flex direction="column" gap={2}>
            <Tooltip
              title={!!prepDetails?.labelOwnerConstraint ? "Amazon has constrained the label owner to be this value - it can't be changed." : ""}
            >
              <Select
                fullWidth
                options={allowedLabelOwners}
                optionRender={option => {
                  switch (option) {
                    case LabelOwner.AMZON:
                      return (
                        <span>Amazon</span>
                      );
                    case LabelOwner.NONE:
                      return (
                        <span>No labelling</span>
                      );
                    case LabelOwner.SELLER:
                      return (
                        <span>Seller</span>
                      );
                  }
                }}
                value={form.labelOwner}
                onChange={v => setForm({...form, labelOwner: v })}
              />
            </Tooltip>
          </Flex>
        </div>
      </form>
    </>
  );

  const tabs: TabItem[] = [
    {
      key: "form",
      label: "Add product",
      children: batchForm
    },
    {
      key: "offers",
      disabled: offersLoading,
      label: offersLoading ? (
        <LoadingSpinner size="1rem" />
      ) : (
        <Flex gap={2} align="center">
          <span>Offers</span>
          <div className={styles.counter}>
            <span>{offers.length}</span>
          </div>
        </Flex>
      ),
      children: (
        <CurrentOffers
          offers={offers}
          offersLoading={offersLoading}
          setPrice={(price: number) => {
            setForm({
              ...form,
              price: {
                price: price.toString(),
              },
            });
            setActiveTab("form");
          }}
        />
      ),
    },
    {
      key: "details",
      label: "Details",
      disabled: offersLoading,
      children: <ProductDetails product={product} offers={offers} />,
    },
  ];
  
  return (
    <Modal
      open={true}
      onOpenChange={() => {}}
      onClose={() => {
        setInvalid(false);
        onClose();
      }}
      closeOnOutsideClick
      className={styles.modal}
    >
      <ModalHeader className={styles.header}>
        <Flex align="center" gap={2}>
          <img src={product?.image || item?.image || "/images/product-placeholder.png"} alt="Product" width="64" height="64" />
          <Flex direction="column" gap={1}>
            <Tooltip title={product?.name ?? item?.name} placement="bottom">
              <span className={`strong ${styles.productTitle}`}>
                {product?.name ?? item?.name}
              </span>
            </Tooltip>

            {(() => {
              // depending on which mode we're in, the properties we want to find may be on different data:
              const asin = product?.asin ?? item?.asin;
              const salesRank = product?.salesRank; // this one can be null right now.
              const sku = product ? product.sellerData[sellerDataIndex].sku : item.sku;
              const condition = product ? product.sellerData[sellerDataIndex].condition : item.condition;
              return (
                <>
                  <Flex align="center" gap={2}>
                    <CopyToClipBoard value={asin} tooltipText="Copy ASIN">
                      <Flex align="center" gap={2}>
                        <label className={"small-text weak"}>ASIN:</label>
                        <span className={"small-text strong"}>{asin}</span>
                      </Flex>
                    </CopyToClipBoard>
                    {(!!salesRank && salesRank !== 0) && (
                      <Flex align="center" gap={2}>
                        <label className={"small-text weak"}>Sales Rank:</label>
                        <span className={"small-text strong"}>{salesRank.toLocaleString("en-US", { style: "decimal", maximumFractionDigits: 2 })}</span>
                      </Flex>
                    )}
                  </Flex>

                  <CopyToClipBoard value={sku} tooltipText="Copy SKU">
                    <Flex align="center" gap={2}>
                      <label className={"small-text weak"}>SKU:</label>
                      <span className={"small-text strong"}>{sku}</span>
                    </Flex>
                  </CopyToClipBoard>
                  <Flex align="center" gap={2}>
                    <label className={"small-text weak"}>Condition:</label>
                    <span className={"small-text strong"}>{productConditionToString(condition)}</span>
                  </Flex>
                </>
              )
            })()}
          </Flex>
        </Flex>
      </ModalHeader>
      <ModalBody className={styles.modalBody}>
        {mode === "ADD_TO_BATCH" && (
          <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={(e) => setActiveTab(e as "form" | "offers" | "details")} />
        )}
        {mode === "UPDATE_ITEM_IN_BATCH" && batchForm}
      </ModalBody>
      <ModalFooter>
        <Flex justify="space-between" align="center">
          <Button
            size="large"
            type="secondary"
            disabled={loading}
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            size="large"
            style={{minWidth:"10rem"}}
            disabled={loading || invalid}
            onClick={() => submit()}
          >
            {mode === "ADD_TO_BATCH" && "Add to Shipment"}
            {mode === "UPDATE_ITEM_IN_BATCH" && "Update Shipment Item"}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

export default FbaBatchItemDrawer;
